// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, type CSSProperties } from 'react'
import { Loader2, ShieldCheck, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: a funnel on every column of the acceptance log.
const CONSENT_LOG_FUNNELS: FunnelColumn<api.AiConsentLogRow>[] = [
  { key: 'accepted_by', label: 'Accepted by', value: r => r.accepted_by ?? '(unknown account)' },
  { key: 'version', label: 'Version', value: r => r.version },
  { key: 'accepted_at', label: 'Accepted at', value: r => r.accepted_at },
]

/**
 * D-CRX-47 POT-5 — admin audit of who accepted the AI training &amp; consent, and when. AI use is gated
 * server-side (carerx/ai/assist returns ai_consent_required until accepted); this is the acceptance trail
 * (carerx_ai_consent). Every AI call is separately logged to carerx_ai_audit with a PHI flag.
 */
export default function CareRxAiConsentLog() {
  const [rows, setRows] = useState<api.AiConsentLogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const fx = useTableFunnels(rows, CONSENT_LOG_FUNNELS)

  const load = () => {
    api.getCareRxAiConsentLog().then(r => { setRows(r.items); setMsg('') }).catch(e => setMsg(friendlyError(e))).finally(() => setLoading(false))
  }
  useEffect(load, [])

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: muted.color, padding: 20 }}><Loader2 size={16} className="spin" /> Loading consent log…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={cardTitle}><ShieldCheck size={16} /> AI training &amp; consent — acceptance log</div>
        <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer' }}><RefreshCw size={14} /></button>
      </div>
      <p style={{ ...muted, marginTop: -4 }}>
        AI assistance is blocked until a user accepts the AI-use training &amp; consent. {rows.length} acceptance{rows.length === 1 ? '' : 's'} on record.
      </p>
      {msg && <div style={{ fontSize: 13, color: '#b91c1c' }}>{msg}</div>}
      <div style={card}>
        {fx.chips}
        <table {...fx.tableProps} style={table}>
          <thead><tr><th style={th}>Accepted by {fx.funnel('accepted_by')}</th><th style={th}>Version {fx.funnel('version')}</th><th style={th}>Accepted at {fx.funnel('accepted_at')}</th></tr></thead>
          <tbody>
            {fx.rows.length === 0 && <tr><td style={td} colSpan={3}><span style={muted}>{rows.length === 0 ? 'No one has accepted yet.' : 'No rows match the current filters.'}</span></td></tr>}
            {fx.rows.map((r, i) => (
              <tr key={i}>
                <td style={td}><b>{r.accepted_by ?? '(unknown account)'}</b></td>
                <td style={td}><code style={chip}>{r.version}</code></td>
                <td style={td}>{formatUsDate(r.accepted_at)} · {new Date(r.accepted_at).toLocaleTimeString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const muted: CSSProperties = { color: 'var(--color-text-soft, #6b7280)' }
const cardTitle: CSSProperties = { fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }
const card: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: '4px 8px' }
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }
const th: CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--color-text-soft, #6b7280)', borderBottom: '1px solid var(--color-border, #e5e7eb)' }
const td: CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-border, #f1f5f9)' }
const chip: CSSProperties = { fontSize: 11, background: 'var(--color-surface-soft, #f2f5f7)', borderRadius: 3, padding: '0 5px' }
